class ProductViewer extends HTMLElement {
  constructor() {
    super();
    this.colorButtons = this.querySelectorAll('.product-viewer__color-btn');
    this.bindEvents();
  }

  bindEvents() {
    this.colorButtons.forEach((button) => {
      button.addEventListener('click', (event) => {
        const currentButton = event.currentTarget;
        const blockId = currentButton.getAttribute('data-block-id');

        this.colorButtons.forEach((btn) => {
          const isActive = btn === currentButton;
          btn.classList.toggle('active', isActive);
          btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        });

        this.changeCurrentImageViewer(blockId);
        this.updateColorName(currentButton.getAttribute('data-color-name'));
      });
    });
  }

  updateColorName(colorName) {
    const colorNameContent = this.querySelector('#color-name-content');
    if (colorNameContent) colorNameContent.textContent = colorName || '';
  }

  changeCurrentImageViewer(blockId) {
    const blockViewer = this.querySelector(`.product-viewer__image-item[data-block-id="${blockId}"]`);
    if (!blockViewer) return;

    const currentActiveViewer = this.querySelector('.product-viewer__image-item.active image-viewer-360');
    if (currentActiveViewer && typeof currentActiveViewer.resetToFirstImage === 'function') {
      currentActiveViewer.resetToFirstImage();
      currentActiveViewer.setAttribute('data-active', 'false');
    }

    this.querySelectorAll('.product-viewer__image-item').forEach((imageViewer) => {
      imageViewer.classList.remove('active');
    });

    blockViewer.classList.add('active');

    const imageViewer360 = blockViewer.querySelector('image-viewer-360');
    if (imageViewer360) imageViewer360.setAttribute('data-active', 'true');
  }
}

class ProductViewer360 extends HTMLElement {
  static get observedAttributes() {
    return ['data-active'];
  }

  static buildImageUrlsFromConfig(config) {
    if (!config) return [];

    const start = parseInt(config.start, 10);
    const end = parseInt(config.end, 10);
    const padding = parseInt(config.padding, 10) || 0;
    const prefix = config.prefix || '';
    const suffix = config.suffix || '';

    if (Number.isNaN(start) || Number.isNaN(end) || end < start) return [];

    const urls = [];
    const paddingSeed = '0000000000';

    for (let frameNumber = start; frameNumber <= end; frameNumber += 1) {
      let frameKey = String(frameNumber);
      if (padding > 0) {
        frameKey = (paddingSeed + frameNumber).slice(-padding);
      }
      urls.push(`${prefix}${frameKey}${suffix}`);
    }

    return urls;
  }

  constructor() {
    super();
    this.options = {
      autoplay: this.dataset.autoplay === 'true',
      speed: parseInt(this.dataset.speed, 10) || 100,
      sensitivity: parseInt(this.dataset.sensitivity, 10) || 10,
      reverse: this.dataset.reverse === 'true',
      clickToHover: this.dataset.clickToHover === 'true',
      hoverReenableDelay: 1500,
      active: this.dataset.active === 'true',
      hasLoadedImages: false
    };
    this.state = {
      currentImageIndex: 0,
      isPlaying: false,
      isDragging: false,
      isHoverDisabled: false,
      initialClick: false,
      isTouching: false,
      hasUserInteracted: false,
      isHorizontalSwipe: null
    };
    this.activePointerId = null;
    this.lastPointerX = 0;
    this.dragResidualX = 0;
    this.touchStartX = 0;
    this.touchStartY = 0;
    this.directionLockThreshold = 10;
    this.velocityX = 0;
    this.velocityHistory = [];
    this.lastMoveX = 0;
    this.lastMoveTime = 0;
    this.momentumRaf = null;
    this.momentumStartTime = 0;
    this.timeouts = {
      hoverRenable: null,
      playInterval: null
    };
    this.eventsAdded = false;
    this.setupPromise = null;

    if (this.options.active) this.run();
  }

  connectedCallback() {
    if (this.dataset.active === 'true') this.run();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue || name !== 'data-active') return;
    if (newValue === 'true') {
      this.resetToFirstImage();
      this.run();
    } else {
      this.pause();
      this.updateCursorState('hide');
    }
  }

  resetToFirstImage() {
    this.stopMomentum();
    this.state.currentImageIndex = 0;
    this.dragResidualX = 0;
    if (this.options.hasLoadedImages && this.images && this.images.length > 0) {
      this.showImage(0);
    } else if (this.imageElement) {
      const firstUrl = this.getImageUrls()[0];
      if (firstUrl) this.imageElement.src = firstUrl;
    }
    if (this.state.isPlaying) this.pause();
  }

  run() {
    if (this.setupPromise) return;
    const setup = () => {
      this.setupPromise = this.setup().finally(() => {
        this.setupPromise = null;
      });
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', setup, { once: true });
    } else {
      setup();
    }
  }

  async setup() {
    this.imageElement = this.querySelector('.image-viewer-360__image');
    this.loadingElement = this.querySelector('.image-viewer-360__loading');
    this.progressFill = this.querySelector('.image-viewer-360__progress-fill');
    this.cursor = this.querySelector('.image-viewer-360__cursor');

    await this.preloadImages();

    if (this.options.hasLoadedImages) {
      this.addEventListeners();
      this.updateCursorState(this.isMobile() ? 'hide' : 'grab');
      if (this.options.autoplay) this.play();
    }
  }

  getFrameConfig() {
    const raw = this.dataset.frameConfig;
    if (!raw) return null;

    try {
      return JSON.parse(raw);
    } catch (error) {
      console.warn('360 Viewer: invalid frame config', error);
      return null;
    }
  }

  getExpectedFrameCount() {
    const config = this.getFrameConfig();
    if (!config) return null;

    const start = parseInt(config.start, 10);
    const end = parseInt(config.end, 10);
    if (Number.isNaN(start) || Number.isNaN(end) || end < start) return null;

    return end - start + 1;
  }

  getImageUrls() {
    const config = this.getFrameConfig();
    if (config) {
      const urls = ProductViewer360.buildImageUrlsFromConfig(config);
      if (urls.length > 0) return urls;
    }

    if (this.dataset.imageUrls) {
      return this.dataset.imageUrls.split(',').map((url) => url.trim()).filter(Boolean);
    }

    return [];
  }

  async preloadImages() {
    if (this.options.hasLoadedImages) return;

    const imageUrls = this.getImageUrls();
    const expectedCount = this.getExpectedFrameCount();

    this.loadedImages = 0;
    this.totalImages = imageUrls.length;
    this.successfullyLoadedImages = 0;
    this.failedImages = 0;
    this.failedImageUrls = [];
    this.images = new Array(imageUrls.length);

    if (imageUrls.length === 0) {
      console.warn('No image URLs provided for the image viewer 360');
      this.hideLoading();
      return;
    }

    if (expectedCount !== null && imageUrls.length !== expectedCount) {
      console.warn(
        `360 Viewer: expected ${expectedCount} frame URLs, generated ${imageUrls.length}`
      );
    }

    this.showLoading();
    await this.loadImagesWithConcurrency(imageUrls, 6);
    this.hideLoading();

    if (this.failedImageUrls.length > 0) {
      console.warn('360 Viewer: failed to load images', this.failedImageUrls);
    }

    if (!this.isImagesFullyLoaded()) {
      console.warn('360 Viewer: Images did not fully load');
    }

    if (this.successfullyLoadedImages <= 1) {
      console.error('360 Viewer: Not enough images loaded, 360 feature disabled');
      return;
    }

    this.fillMissingFrames();
    this.showImage(0);
    this.options.hasLoadedImages = true;
  }

  fillMissingFrames() {
    const firstLoadedImage = this.images.find(Boolean);
    let previousImage = firstLoadedImage;

    this.images = this.images.map((image) => {
      if (image) {
        previousImage = image;
        return image;
      }

      return previousImage || firstLoadedImage;
    });
  }

  async loadImagesWithConcurrency(urls, limit) {
    const executing = [];
    const results = [];

    for (const [index, url] of urls.entries()) {
      const promise = this.loadSingleImage(url, index).then((image) => {
        executing.splice(executing.indexOf(promise), 1);
        return image;
      });
      results.push(promise);
      executing.push(promise);

      if (executing.length >= limit) await Promise.race(executing);
    }

    return Promise.all(results);
  }

  loadSingleImage(url, index) {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const finishLoad = () => {
          this.loadedImages += 1;
          this.successfullyLoadedImages += 1;
          this.updateLoadingProgress();
          this.images[index] = img;
          resolve(img);
        };

        if (typeof img.decode === 'function') {
          img.decode().then(finishLoad).catch(finishLoad);
        } else {
          finishLoad();
        }
      };
      img.onerror = () => {
        this.loadedImages += 1;
        this.failedImages += 1;
        this.failedImageUrls.push(url);
        this.updateLoadingProgress();
        resolve(null);
      };
      img.src = url;
    });
  }

  isImagesFullyLoaded() {
    return this.loadedImages === this.totalImages && this.totalImages > 0;
  }

  isImagesSuccessfullyLoaded() {
    return this.isImagesFullyLoaded() && this.failedImages === 0;
  }

  getLoadingProgress() {
    return this.totalImages === 0 ? 0 : Math.round((this.loadedImages / this.totalImages) * 100);
  }

  getLoadingStatus() {
    return {
      total: this.totalImages || 0,
      loaded: this.loadedImages || 0,
      successful: this.successfullyLoadedImages || 0,
      failed: this.failedImages || 0,
      progress: this.getLoadingProgress(),
      isComplete: this.isImagesFullyLoaded(),
      isSuccessful: this.isImagesSuccessfullyLoaded()
    };
  }

  updateLoadingProgress() {
    if (!this.progressFill) return;
    this.progressFill.style.width = `${this.getLoadingProgress()}%`;
  }

  showLoading() {
    if (this.progressFill) this.progressFill.style.width = '0%';
    if (this.loadingElement) this.loadingElement.style.display = 'flex';
  }

  hideLoading() {
    if (this.loadingElement) this.loadingElement.style.display = 'none';
  }

  showImage(index) {
    if (index >= 0 && index < this.images.length && this.images[index]) {
      this.state.currentImageIndex = index;
      this.imageElement.src = this.images[index].src;
    }
  }

  addEventListeners() {
    if (this.eventsAdded) return;
    this.eventsAdded = true;

    this.addEventListener('mouseenter', () => {
      if (this.options.hasLoadedImages && !this.isMobile()) this.updateCursorState('grab');
    });
    this.addEventListener('mouseleave', this.onMouseLeave.bind(this));
    this.addEventListener('mousemove', this.onMouseMove.bind(this));
    this.addEventListener('dragstart', (event) => event.preventDefault());
    this.addEventListener('contextmenu', (event) => event.preventDefault());

    if (window.PointerEvent) {
      this.addEventListener('pointerdown', this.onPointerDown.bind(this));
      this.addEventListener('pointermove', this.onPointerMove.bind(this));
      this.addEventListener('pointerup', this.onPointerUp.bind(this));
      this.addEventListener('pointercancel', this.onPointerUp.bind(this));
    } else {
      this.addEventListener('mousedown', this.onMouseDown.bind(this));
      this.addEventListener('mousemove', this.onMouseMoveDrag.bind(this));
      this.addEventListener('mouseup', this.onMouseUp.bind(this));

      this.addEventListener('touchstart', this.onTouchStart.bind(this), { passive: false });
      this.addEventListener('touchmove', this.onTouchMove.bind(this), { passive: false });
      this.addEventListener('touchend', this.onTouchEnd.bind(this), { passive: true });
      this.addEventListener('touchcancel', this.onTouchEnd.bind(this), { passive: true });
    }
  }

  onMouseLeave() {
    this.updateCursorState('hide');
    this.stopMomentum();
    if (this.options.autoplay) this.play();
  }

  updateCursorState(state) {
    if (!this.cursor) return;

    const cursorStates = {
      hide: { text: '', cursor: 'pointer', opacity: 0 },
      grab: { text: 'Grab', cursor: 'grab', opacity: 0.8 },
      grabbing: { text: 'Grab', cursor: 'grabbing', opacity: 0.8 }
    };
    const { text, cursor, opacity } = cursorStates[state] || cursorStates.hide;

    this.cursor.textContent = text;
    this.style.cursor = cursor;
    this.cursor.style.opacity = opacity;
    this.cursor.classList.toggle('image-viewer-360__cursor--active', state === 'grabbing');
    this.cursor.style.display = state === 'hide' ? 'none' : 'flex';
  }

  moveCursor(event) {
    if (!this.cursor || this.isMobile() || this.state.isDragging) return;
    const rect = this.getBoundingClientRect();
    const targetX = (event.clientX ?? 0) - rect.left;
    const targetY = (event.clientY ?? 0) - rect.top;
    this.cursor.style.left = `${targetX}px`;
    this.cursor.style.top = `${targetY}px`;
  }

  onMouseMove(event) {
    this.moveCursor(event);
    if (!this.state.isDragging && !this.momentumRaf) this.pauseAutoplay();
  }

  resetDragVelocity(clientX) {
    this.velocityX = 0;
    this.velocityHistory = [];
    this.lastMoveX = clientX;
    this.lastMoveTime = performance.now();
  }

  trackDragVelocity(clientX) {
    const currentTime = performance.now();
    const timeDelta = currentTime - this.lastMoveTime;

    if (timeDelta > 0 && timeDelta < 100) {
      const moveDelta = clientX - this.lastMoveX;
      const instantVelocity = moveDelta / timeDelta;
      const maxHistory = this.isMobile() ? 8 : 5;

      this.velocityHistory.push(instantVelocity);
      if (this.velocityHistory.length > maxHistory) {
        this.velocityHistory.shift();
      }

      const historyLength = this.velocityHistory.length;
      let totalWeight = 0;
      this.velocityX = this.velocityHistory.reduce((sum, v, i) => {
        const weight = this.isMobile()
          ? Math.pow(0.7, historyLength - 1 - i)
          : [0.1, 0.15, 0.2, 0.25, 0.3][i] ?? 0.2;
        totalWeight += weight;
        return sum + v * weight;
      }, 0);

      if (totalWeight > 0) {
        this.velocityX /= totalWeight;
      }
    }

    this.lastMoveX = clientX;
    this.lastMoveTime = currentTime;
  }

  stopMomentum() {
    if (this.momentumRaf) {
      cancelAnimationFrame(this.momentumRaf);
      this.momentumRaf = null;
    }
  }

  startMomentum() {
    if (!this.options.hasLoadedImages || !this.images?.length) return;

    const minVelocity = this.isMobile() ? 0.16 : 0.18;
    if (Math.abs(this.velocityX) < minVelocity) return;

    this.stopMomentum();

    const velocityScale = 0.55;
    let velocity = this.velocityX * velocityScale;
    const friction = 0.86;
    const maxDuration = 650;
    let lastTime = performance.now();
    this.momentumStartTime = lastTime;

    const animate = (now) => {
      const dt = Math.min(now - lastTime, 32);
      lastTime = now;

      if (dt > 0) {
        this.rotateByDelta(velocity * dt);
        velocity *= Math.pow(friction, dt / 16);
      }

      if (
        Math.abs(velocity) > minVelocity &&
        now - this.momentumStartTime < maxDuration
      ) {
        this.momentumRaf = requestAnimationFrame(animate);
      } else {
        this.stopMomentum();
      }
    };

    this.momentumRaf = requestAnimationFrame(animate);
  }

  onPointerDown(event) {
    if (event.button != null && event.button !== 0) return;

    event.preventDefault();
    this.stopMomentum();
    this.state.initialClick = true;
    this.state.isDragging = true;
    this.activePointerId = event.pointerId;
    this.lastPointerX = event.clientX;
    this.dragResidualX = 0;
    this.resetDragVelocity(event.clientX);
    this.state.isHoverDisabled = true;
    clearTimeout(this.timeouts.hoverRenable);
    this.updateCursorState('grabbing');
    this.pauseAutoplay();

    try {
      this.setPointerCapture(event.pointerId);
    } catch (_) {
      /* ignore */
    }
  }

  onPointerMove(event) {
    if (!this.state.isDragging || this.activePointerId !== event.pointerId) return;

    const deltaX = event.clientX - this.lastPointerX;
    this.rotateByDelta(deltaX);
    this.trackDragVelocity(event.clientX);
    this.lastPointerX = event.clientX;
    event.preventDefault();
  }

  onPointerUp(event) {
    if (this.activePointerId != null && event.pointerId !== this.activePointerId) return;

    this.endDrag();
    try {
      this.releasePointerCapture(event.pointerId);
    } catch (_) {
      /* ignore */
    }
  }

  onMouseDown(event) {
    if (event.button != null && event.button !== 0) return;

    event.preventDefault();
    this.stopMomentum();
    this.state.initialClick = true;
    this.state.isDragging = true;
    this.activePointerId = 'mouse';
    this.lastPointerX = event.clientX;
    this.dragResidualX = 0;
    this.resetDragVelocity(event.clientX);
    this.updateCursorState('grabbing');
    this.pauseAutoplay();
  }

  onMouseMoveDrag(event) {
    if (!this.state.isDragging || this.activePointerId !== 'mouse') return;

    const deltaX = event.clientX - this.lastPointerX;
    this.rotateByDelta(deltaX);
    this.trackDragVelocity(event.clientX);
    this.lastPointerX = event.clientX;
  }

  onMouseUp() {
    if (this.activePointerId !== 'mouse') return;
    this.endDrag();
  }

  onTouchStart(event) {
    if (!event.touches || event.touches.length === 0) return;

    const touch = event.touches[0];
    this.stopMomentum();
    this.updateCursorState('hide');
    this.state.hasUserInteracted = true;
    this.state.initialClick = true;
    this.state.isTouching = true;
    this.state.isDragging = true;
    this.activePointerId = touch.identifier;
    this.touchStartX = touch.clientX;
    this.touchStartY = touch.clientY;
    this.lastPointerX = touch.clientX;
    this.dragResidualX = 0;
    this.resetDragVelocity(touch.clientX);
    this.state.isHorizontalSwipe = null;
    this.pauseAutoplay();
    event.preventDefault();
  }

  onTouchMove(event) {
    if (!this.state.isDragging || !event.touches || event.touches.length === 0) return;

    const touch =
      Array.from(event.touches).find((t) => t.identifier === this.activePointerId) || event.touches[0];
    const currentX = touch.clientX;
    const currentY = touch.clientY;
    const diffX = Math.abs(currentX - this.touchStartX);
    const diffY = Math.abs(currentY - this.touchStartY);

    if (
      this.state.isHorizontalSwipe === null &&
      (diffX > this.directionLockThreshold || diffY > this.directionLockThreshold)
    ) {
      this.state.isHorizontalSwipe = diffX > diffY;
    }

    if (this.state.isHorizontalSwipe === true) {
      if (event.cancelable) event.preventDefault();
      const deltaX = currentX - this.lastPointerX;
      this.rotateByDelta(deltaX);
      this.trackDragVelocity(currentX);
      this.lastPointerX = currentX;
    }
  }

  onTouchEnd() {
    this.state.isTouching = false;
    this.state.isHorizontalSwipe = null;
    this.endDrag(true);
  }

  endDrag(hideCursor = false) {
    this.state.isDragging = false;
    this.activePointerId = null;
    this.dragResidualX = 0;
    this.updateCursorState(hideCursor ? 'hide' : this.isMobile() ? 'hide' : 'grab');
    this.timeouts.hoverRenable = setTimeout(() => {
      this.state.isHoverDisabled = false;
    }, this.options.hoverReenableDelay);
    this.startMomentum();
  }

  rotateByDelta(deltaX) {
    if (this.options.clickToHover && !this.state.initialClick) return;
    if (!this.images?.length) return;

    const width = this.clientWidth || 300;
    const pxPerFrame = width / Math.max(this.images.length, 1);
    const threshold = Math.max(2, pxPerFrame / this.options.sensitivity);

    this.dragResidualX += deltaX;
    const framesDelta = Math.trunc(this.dragResidualX / threshold);

    if (framesDelta === 0) return;

    this.dragResidualX -= framesDelta * threshold;

    let newIndex = this.state.currentImageIndex - framesDelta;
    newIndex = ((newIndex % this.images.length) + this.images.length) % this.images.length;
    this.showImage(newIndex);
  }

  rotateImage(direction) {
    if (!this.images.length) return;
    const newIndex =
      (this.state.currentImageIndex + direction + this.images.length) % this.images.length;
    this.showImage(newIndex);
  }

  play() {
    if (this.momentumRaf) return;
    if (!this.state.isPlaying) {
      this.timeouts.playInterval = setInterval(() => {
        this.rotateImage(this.options.reverse ? -1 : 1);
      }, this.options.speed);
      this.state.isPlaying = true;
    }
  }

  pauseAutoplay() {
    clearInterval(this.timeouts.playInterval);
    this.state.isPlaying = false;
  }

  pause() {
    this.stopMomentum();
    this.pauseAutoplay();
  }

  isMobile() {
    return window.innerWidth <= 900;
  }
}

if (!customElements.get('product-viewer')) {
  customElements.define('product-viewer', ProductViewer);
}

if (!customElements.get('image-viewer-360')) {
  customElements.define('image-viewer-360', ProductViewer360);
}
